import React, { useState } from "react";
import { initials, prettyRole } from "../helpers";
import { Icon } from "./ui";

const PROCESS_LABEL = { SUBMITTER: "Submitter", UPLOADER: "Uploader", MODIFIER: "Modifier", MORE_INFO: "More Info" };

// does this node (or any descendant) contain the active leaf?
const hasActiveLeaf = (n, active) => n.children ? n.children.some((c) => hasActiveLeaf(c, active)) : n.key === active;
// keys of every expandable node on the path to the active leaf (so ancestors auto-open)
function openPath(items, active, out = {}) {
  items.forEach((n) => {
    if (n.children && hasActiveLeaf(n, active)) { out[n.key] = true; openPath(n.children, active, out); }
  });
  return out;
}
// the chain of keys from the top down to `key` (inclusive), or null if not found — used for accordion open/close
function chainTo(items, key, trail = []) {
  for (const n of items) {
    const here = [...trail, n.key];
    if (n.key === key) return here;
    if (n.children) { const r = chainTo(n.children, key, here); if (r) return r; }
  }
  return null;
}

export function Sidebar({ user, nav, active, onNavigate, collapsed, client, process, canSwitch, onSwitch }) {
  // which expandable groups are open; open the ones on the path to the active child by default
  const [open, setOpen] = useState(() => openPath(nav, active));
  const [profileOpen, setProfileOpen] = useState(false);
  // Navigating to any leaf collapses every dropdown except the one on the path to it, so opening a tab in another
  // section automatically closes the group you had expanded before.
  React.useEffect(() => {
    setOpen(openPath(nav, active));
  }, [active, nav]);

  const renderNode = (n, depth) => {
    const Ico = n.icon;
    if (n.children) {
      const isOpen = !!open[n.key];
      const childActive = hasActiveLeaf(n, active);
      // Accordion: opening a group keeps only the chain down to it open (so sibling groups and other sections close);
      // closing it collapses it and everything under it, leaving its ancestors open.
      const toggle = () => setOpen(() => {
        const chain = chainTo(nav, n.key) || [n.key];
        const keep = isOpen ? chain.slice(0, -1) : chain;
        const next = {};
        keep.forEach((k) => { next[k] = true; });
        return next;
      });
      return (
        <div key={n.key} className="nav-group">
          {depth === 0 ? (
            <button className={"nav-item" + (childActive ? " active-parent" : "")} onClick={toggle} title={n.label}>
              <span className="ico"><Ico /></span>
              <span className="lbl">{n.label}</span>
              <span className={"nav-caret" + (isOpen ? " up" : "")}><Icon.chevron width="16" height="16" /></span>
            </button>
          ) : (
            <button className={"nav-child" + (childActive ? " active-parent" : "")} onClick={toggle} title={n.label}
              style={{ justifyContent: "space-between" }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}><span className="dot" />{n.label}</span>
              <span className={"nav-caret" + (isOpen ? " up" : "")}><Icon.chevron width="14" height="14" /></span>
            </button>
          )}
          {isOpen && <div className="nav-children">{n.children.map((c) => renderNode(c, depth + 1))}</div>}
        </div>
      );
    }
    if (depth === 0) {
      return (
        <button key={n.key} className={"nav-item" + (active === n.key ? " active" : "")} onClick={() => onNavigate(n.key)} title={n.label}>
          <span className="ico"><Ico /></span>
          <span className="lbl">{n.label}</span>
          {typeof n.count === "number" && n.count > 0 && <span className="nav-n">{n.count}</span>}
        </button>
      );
    }
    return (
      <button key={n.key} className={"nav-child" + (active === n.key ? " active" : "")} onClick={() => onNavigate(n.key)} title={n.label}>
        <span className="dot" />{n.label}
        {typeof n.count === "number" && <span className="child-n">{n.count}</span>}
      </button>
    );
  };

  return (
    <aside className={"sidebar" + (collapsed ? " collapsed" : "")}>
      <img className="side-bg-img" src="/images/sidebar.png" alt="" aria-hidden="true"
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="side-bg-overlay" aria-hidden="true" />
      <nav className="nav">
        <div className="nav-label">Workspace</div>
        {nav.map((n) => renderNode(n, 0))}
      </nav>

      <div className="side-user-wrap">
        {profileOpen && <div className="side-user-backdrop" onClick={() => setProfileOpen(false)} />}
        {profileOpen && canSwitch && (
          <div className="profile-menu">
            <button className="profile-menu-item" onClick={() => { setProfileOpen(false); onSwitch && onSwitch(); }}>
              <Icon.history width="15" height="15" /> Switch client / process
            </button>
          </div>
        )}
        <button className="side-user" onClick={() => canSwitch && setProfileOpen((o) => !o)} title={canSwitch ? "Switch client / process" : "Profile"} style={canSwitch ? undefined : { cursor: "default" }}>
          <div className="avatar">{initials(user.fullName)}</div>
          <div className="who">
            <b>{user.fullName}</b>
            <span>{client ? client : prettyRole(user.role)}{process ? ` · ${PROCESS_LABEL[process] || process}` : ""}</span>
          </div>
          {canSwitch && <span className={"side-user-caret" + (profileOpen ? " open" : "")}><Icon.chevron width="15" height="15" /></span>}
        </button>
      </div>
    </aside>
  );
}